import { HttpClient, HttpEvent } from '@angular/common/http';
import { map, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import { AppConfigService } from '../../services/app-config.service';
import { RecoveryManager } from '../../managers/recovery.manager';
import { ODataRequest } from './o-data-request';

@Injectable({
	providedIn: 'root'
})
export class SdkBase {

	constructor(private injector: Injector) { }
	protected httpClient = this.injector.get(HttpClient);

	protected getApiRoot(): string {
		const config = this.injector.get(AppConfigService);
		return config.getConfig().webApi;
	}

	protected makeODataRequest$<TResponse>(request: ODataRequest<any>, prefix: string = 'odata'): Observable<TResponse> {
		const http = this.injector.get(HttpClient);
		const config = this.injector.get(AppConfigService);
		const recoveryManager = this.injector.get(RecoveryManager);

		if (!request?.entityUrl) {
			return throwError(() => new Error('A request url must be provided to access an OData endpoint.'));
		}

		let requestUrl = `${config.getConfig().webApi}/${prefix}/${request.entityUrl}`;
		const requestOptions = {
			params: request.requestParams
		};

		switch (request.method) {
			case 'get': {
				return http.get<TResponse>(requestUrl, requestOptions).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed OData request: [GET] ${request.entityUrl}`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			case 'put': {
				if (request.postBodyOrResourceId) {
					requestUrl += `/${request.postBodyOrResourceId}`;
				}

				return http.put<TResponse>(requestUrl, request.putBody, requestOptions).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed OData request: [PUT] ${request.entityUrl}`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);

			}
			case 'post': {
				return http.post<TResponse>(requestUrl, request.postBodyOrResourceId, requestOptions).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed OData request: [POST] ${request.entityUrl}`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			case 'patch': {
				if (request.postBodyOrResourceId) {
					requestUrl += `/${request.postBodyOrResourceId}`;
				}

				return http.patch<TResponse>(requestUrl, request.putBody, requestOptions).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed OData request: [PATCH] ${request.entityUrl}`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			case 'delete': {
				if (request.postBodyOrResourceId) {
					requestUrl += `/${request.postBodyOrResourceId}`;
				}

				return http.delete(requestUrl, requestOptions).pipe(
					map(() => null),
					catchError((requestError) =>
						recoveryManager.handleError({
							message: requestError?.error?.error?.message,
							errorType: 'recoverable',
							internalError: requestError
						})
					)
				);
			}
		}
	}

	protected makefileRequest$(path: string, method: 'get' | 'post' = 'get', data?: any): Observable<Blob> {
		const http = this.injector.get(HttpClient);
		const config = this.injector.get(AppConfigService);
		const recoveryManager = this.injector.get(RecoveryManager);
		const url = `${config.getConfig().webApi}/api/${path}`;

		switch (method) {
			case 'post': {
				return http.post<Blob>(url, data, { responseType: 'blob' as 'json' }).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed to retrieve file ${url}`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			default: {
				return http.get<Blob>(url, { responseType: 'blob' as 'json' }).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed to retrieve file ${url}`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
		}
	}

	protected makeHttpRequest<T>(method: 'delete', path: string): Observable<T>;
	protected makeHttpRequest<T>(method: 'get', path: string): Observable<T>;
	protected makeHttpRequest<T>(method: 'post' | 'put', path: string, body: any): Observable<T>;
	protected makeHttpRequest<T>(method: 'put', path: string, body: any): Observable<T>;
	protected makeHttpRequest<T>(method: 'patch', path: string, body: any): Observable<T>;
	protected makeHttpRequest<T>(method: 'get' | 'post' | 'put' | 'patch' | 'delete', path: string, body?: any): Observable<T> {
		const http = this.injector.get(HttpClient);
		const config = this.injector.get(AppConfigService);
		const recoveryManager = this.injector.get(RecoveryManager);
		const url = path.includes('://') ? path : `${config.getConfig().webApi}/${path}`;

		switch (method) {
			case 'get': {
				return http.get<any>(url).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed to do`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			case 'post': {
				return http.post<any>(url, body).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed to do`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			case 'put': {
				return http.put<any>(url, body).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: `Failed to do`,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			case 'patch': {
				return http.patch<any>(url, body).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: error?.error?.message,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
			case 'delete': {
				return http.delete<any>(url).pipe(
					catchError((error) =>
						recoveryManager.handleError({
							message: error?.error?.message,
							errorType: 'recoverable',
							internalError: error
						})
					)
				);
			}
		}
	}

	protected getHttpStreamRequest<T>(path: string): Observable<HttpEvent<T>> {
		const http = this.injector.get(HttpClient);
		const config = this.injector.get(AppConfigService);
		const recoveryManager = this.injector.get(RecoveryManager);
		const url = path.includes('://') ? path : `${config.getConfig().webApi}/${path}`;

		return http.get<any>(url, { observe: 'events' }).pipe(
			catchError((error) =>
				recoveryManager.handleError({
					message: `Failed to do`,
					errorType: 'recoverable',
					internalError: error
				})
			)
		);
	}
}

